import {
  Box,
  Divider,
  makeStyles,
  Popover,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import {
  ADDITIONAL_BILLING_STATUS,
  getAdditionalBillingDisplayStatus,
  getChipDisplayClass,
  getDisplayStatus,
  LOAD_STATUS,
} from 'doc-mate-store/lib/constants/load';
import React, { useState } from 'react';

export const LegendChip = styled('div')({
  display: 'inline-block',
  padding: '0px 8px',
  height: '20px',
  borderRadius: '10px',
  lineHeight: '20px',
  fontSize: '11px',
  fontWeight: 600,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textAlign: 'center',

  '&.unassigned, &.denied, &.billing_issue': {
    backgroundColor: '#F38686',
    color: 'white',
  },
  '&.pickup, &.pending': {
    backgroundColor: '#FADFA3',
    color: 'black',
  },
  '&.dropoff, &.approved': {
    backgroundColor: '#A6E7C1',
    color: 'black',
  },
  '&.delivered, &.invoiced': {
    backgroundColor: '#CAA2EE',
    color: 'black',
  },
  '&.completed': {
    backgroundColor: '#ACD5F0',
    color: 'black',
  },
  '&.cancelled': {
    backgroundColor: '#BDBDBD',
    color: 'black',
  },
});

export type TableConfig = {
  key: string;
  label: string;
  group: string;
  description: string;
};

export const TABLES_CONFIG: TableConfig[] = [
  // Summary
  {
    key: 'all',
    label: 'All',
    group: 'Summary',
    description: 'Shows all loads across all statuses',
  },
  {
    key: 'active',
    label: 'Active',
    group: 'Summary',
    description: 'Shows loads currently en route or in progress',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    group: 'Summary',
    description: 'Shows loads delivered and waiting to be verified',
  },
  {
    key: 'completed',
    label: 'Dispatch Cleared',
    group: 'Summary',
    description: 'Shows loads fully completed and cleared by dispatch',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    group: 'Summary',
    description: 'Shows loads that were declined or rejected by drivers',
  },

  // Assignments
  {
    key: 'assignment_unassigned',
    label: 'Unassigned',
    group: 'Assignments',
    description: 'Shows loads requiring driver assignment',
  },
  {
    key: 'assignment_assigned',
    label: 'Assigned',
    group: 'Assignments',
    description: 'Shows loads currently assigned to a driver',
  },
  {
    key: 'stale',
    label: 'Stale',
    group: 'Assignments',
    description:
      'Shows loads assigned to a driver, not in progress, and is past the drop-off period',
  },

  // Pick-up
  {
    key: 'pickup_arrived',
    label: 'Pickup Arrived',
    group: 'Pick-up',
    description:
      'Shows active loads where driver has arrived at the pickup site',
  },
  {
    key: 'pickup_missed',
    label: 'Pickup Delayed',
    group: 'Pick-up',
    description: 'Shows active loads past the pick-up period',
  },

  // Drop-off
  {
    key: 'dropoff_arrived',
    label: 'Dropoff Arrived',
    group: 'Drop-off',
    description:
      'Shows active loads where driver has arrived at the dropoff site',
  },
  {
    key: 'dropoff_missed',
    label: 'Dropoff Delayed',
    group: 'Drop-off',
    description: 'Shows active loads past the drop-off period',
  },

  // Billing
  {
    key: 'dispatch_verified',
    label: 'Dispatch Verified',
    group: 'Billing',
    description: 'Shows loads verified by dispatch and ready for billing',
  },
  {
    key: 'billing_issue',
    label: 'Billing Issue',
    group: 'Billing',
    description:
      'Shows loads flagged with missing paperwork or billing discrepancies',
  },
  {
    key: 'invoiced',
    label: 'Invoiced',
    group: 'Billing',
    description: 'Shows loads that have been processed and invoiced',
  },

  // Additional Billing
  {
    key: 'additional_billing_all',
    label: 'Additional Billing - All',
    group: 'Additional Billing',
    description: 'Shows all accessorial and additional billing',
  },
  {
    key: 'additional_billing_pending',
    label: 'Additional Billing - Pending',
    group: 'Additional Billing',
    description: 'Shows additional billing requests awaiting review',
  },
  {
    key: 'additional_billing_billing_issue',
    label: 'Additional Billing - Billing Issue',
    group: 'Additional Billing',
    description:
      'Shows additional billing flagged with missing paperwork or billing discrepancies',
  },
  {
    key: 'additional_billing_approved',
    label: 'Additional Billing - Approved',
    group: 'Additional Billing',
    description: 'Shows additional billing requests approved for invoicing',
  },
  {
    key: 'additional_billing_denied',
    label: 'Additional Billing - Denied',
    group: 'Additional Billing',
    description: 'Shows additional billing requests denied by review',
  },
  {
    key: 'additional_billing_invoiced',
    label: 'Additional Billing - Invoiced',
    group: 'Additional Billing',
    description: 'Shows additional billing items that have been invoiced',
  },
];

const LOAD_STATUS_DESCRIPTIONS: Record<string, string> = {
  [LOAD_STATUS.UNASSIGNED]: 'Load is not yet assigned to a driver',
  [LOAD_STATUS.ASSIGNED]: 'Driver is assigned to the load',
  [LOAD_STATUS.PICKUP_GOING]: 'Driver is en-route to the pickup location',
  [LOAD_STATUS.PICKUP_ARRIVED]: 'Driver is at pickup location',
  [LOAD_STATUS.DROPOFF_GOING]: 'Driver is en-route to the dropoff location',
  [LOAD_STATUS.DROPOFF_ARRIVED]: 'Driver is at dropoff location',
  [LOAD_STATUS.DELIVERED]: 'Load has been delivered to destination',
  [LOAD_STATUS.COMPLETED]: 'Dispatch has manually updated the load as cleared.',
  [LOAD_STATUS.DISPATCH_VERIFIED]:
    'Dispatch verified all load details and documents',
  [LOAD_STATUS.BILLING_ISSUE]: 'Paperwork or billing issue flagged',
  [LOAD_STATUS.INVOICED]: 'Load has been invoiced',
  [LOAD_STATUS.CANCELLED]: 'Load has been cancelled',
};

const ADDITIONAL_BILLING_DESCRIPTIONS: Record<string, string> = {
  [ADDITIONAL_BILLING_STATUS.PENDING]: 'Additional Billing is pending approval',
  [ADDITIONAL_BILLING_STATUS.BILLING_ISSUE]:
    'Paperwork or billing issue flagged',
  [ADDITIONAL_BILLING_STATUS.APPROVED]: 'Additional Billing has been approved',
  [ADDITIONAL_BILLING_STATUS.DENIED]: 'Additional Billing has been denied',
  [ADDITIONAL_BILLING_STATUS.INVOICED]: 'Additional Billing has been invoiced',
};

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

export const StatusLegendPopover: React.FC<Props> = ({ anchorEl, onClose }) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const open = Boolean(anchorEl);

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  // Group Table Views by group name
  const groupedTableConfigs = TABLES_CONFIG.reduce((acc, config) => {
    if (!acc[config.group]) acc[config.group] = [];
    acc[config.group].push(config);
    return acc;
  }, {} as Record<string, TableConfig[]>);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      classes={{ paper: classes.popoverPaper }}
    >
      <Box className={classes.tabContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Statuses" />
          <Tab label="Table Filters" />
        </Tabs>
      </Box>

      <div className={classes.scrollContent}>
        {activeTab === 0 && (
          <div className={classes.container}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Load Statuses
            </Typography>
            <div className={classes.statusList}>
              {Object.keys(LOAD_STATUS_DESCRIPTIONS).map(stat => (
                <div key={stat} className={classes.statusRow}>
                  <div className={classes.chipWrapper}>
                    <LegendChip
                      className={getChipDisplayClass(stat as LOAD_STATUS)}
                    >
                      {getDisplayStatus(stat as LOAD_STATUS)}
                    </LegendChip>
                  </div>
                  <Typography variant="caption" className={classes.description}>
                    {LOAD_STATUS_DESCRIPTIONS[stat]}
                  </Typography>
                </div>
              ))}
            </div>

            <Divider className={classes.divider} />

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Additional Billing Statuses
            </Typography>
            <div className={classes.statusList}>
              {Object.keys(ADDITIONAL_BILLING_DESCRIPTIONS).map(stat => (
                <div key={stat} className={classes.statusRow}>
                  <div className={classes.chipWrapper}>
                    <LegendChip className={stat}>
                      {getAdditionalBillingDisplayStatus(
                        stat as ADDITIONAL_BILLING_STATUS,
                      )}
                    </LegendChip>
                  </div>
                  <Typography variant="caption" className={classes.description}>
                    {ADDITIONAL_BILLING_DESCRIPTIONS[stat]}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className={classes.container}>
            {Object.entries(groupedTableConfigs).map(
              ([groupName, items], index) => (
                <React.Fragment key={groupName}>
                  {index > 0 && <Divider className={classes.divider} />}
                  <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                  >
                    {groupName}
                  </Typography>
                  <div className={classes.statusList}>
                    {items.map(item => (
                      <div key={item.key} className={classes.tableRow}>
                        <Typography
                          variant="body2"
                          className={classes.tableLabel}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          className={classes.description}
                        >
                          {item.description}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              ),
            )}
          </div>
        )}
      </div>
    </Popover>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  popoverPaper: {
    width: 420,
    maxHeight: 520,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[4],
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  scrollContent: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(1),
  },
  statusList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(0.5),
  },
  tableLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.primary,
  },
  chipWrapper: {
    width: 140,
    flexShrink: 0,
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.3,
  },
  divider: {
    margin: theme.spacing(2, 0, 1.5),
  },
}));

export default React.memo(StatusLegendPopover);
